import { createContext, useCallback, useMemo, useRef, MouseEvent, memo } from 'react'
import MuiTableCell, { TableCellProps } from '@mui/material/TableCell'
import Tooltip from '@mui/material/Tooltip'
import { abbreviateTitle } from '@common/helpers/abbreviateTitle'
import { CellNoteConfig } from '@common/interfaces/notes'
import cn from 'classnames'
import CellWithNotes from '../CellNotes/CellWithNotes'
import { useIsDenseTable } from '../Table/DenseTableContext'
import { useIsTableHead } from '../TableHead/TableSectionContext'
import styles from './TableCell.module.scss'
import { useTableCellManager } from './TableCellManager'

// Define the context type
interface TableCellContextType {
  isActive: boolean
  resetActiveCell: () => void
}

// Create the context
export const TableCellContext = createContext<TableCellContextType | undefined>(undefined)

interface IProps extends TableCellProps {
  isAutocompleteFilter?: boolean
  isAmountFilter?: boolean
  isDateFilter?: boolean
  isTextFilter?: boolean
  cellNoteConfig?: CellNoteConfig
}

const TableCell = memo(
  ({
    isAutocompleteFilter,
    isAmountFilter,
    isDateFilter,
    isTextFilter,
    cellNoteConfig,
    children,
    onClick,
    ...props
  }: IProps) => {
    const { activeCell, setActiveCell } = useTableCellManager()
    const isDense = useIsDenseTable()
    const isTableHead = useIsTableHead()
    const cellRef = useRef<HTMLTableCellElement | null>(null)

    const handleClickInside = useCallback(
      (e: MouseEvent<HTMLTableCellElement>) => {
        onClick?.(e)
        // A modifier-click is a multi-select gesture, not an edit — don't
        // activate the cell (which would open the select dropdown / date picker).
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          return
        }
        const target = e.target as Element
        if (target?.closest?.('.MuiAutocomplete-option, .MuiMenuItem-root, .MuiSelect-select')) {
          return
        }

        setActiveCell(cellRef.current)
      },
      [setActiveCell, onClick],
    )

    const resetActiveCell = useCallback(() => {
      setActiveCell(null)
    }, [setActiveCell])

    const isActive = useMemo(() => {
      return cellRef.current && activeCell === cellRef.current
    }, [activeCell])

    const classNames = useMemo(() => {
      return cn(styles.root, {
        [styles.autocompleteFilter]: isAutocompleteFilter,
        [styles.amountFilter]: isAmountFilter,
        [styles.dateFilter]: isDateFilter,
        [styles.textFilter]: isTextFilter,
      })
    }, [isAutocompleteFilter, isAmountFilter, isDateFilter, isTextFilter])

    // Prepare the cell content
    const cellContent = useMemo(() => {
      // A hand-written header on the dense mobile table. About a third of the tables here
      // do not drive their headers from an IFilter[] — they write the label straight into
      // the cell — so this is what gives those the same abbreviated headers the
      // config-driven ones get from TableFiltersRow, with no per-table edits.
      //
      // The `typeof children === 'string'` guard is what makes it safe: TableFiltersRow
      // passes a <div>, so its cells can never be abbreviated twice, and a cell holding
      // markup is left alone rather than being flattened to text.
      const content =
        isDense && isTableHead && typeof children === 'string' ? (
          // `disableTouchListener={false}` opts back in to the touch tooltips the theme
          // turns off app-wide: this one expands an abbreviated header, so it is there
          // for touch rather than in spite of it. See theme.ts.
          <Tooltip
            title={children}
            placement="top"
            enterTouchDelay={0}
            disableTouchListener={false}
          >
            <span>{abbreviateTitle(children)}</span>
          </Tooltip>
        ) : (
          children
        )

      // Notes are a desktop affordance, and on the dense mobile table they are the single
      // biggest cost on the page. `CellWithNotes` is redux-connected and carries four
      // pieces of local state plus `useLocation`/`useParams`/`useNavigate` — per cell. A
      // financials table is ~40 rows x 12 month columns, so that is ~500 connected
      // components re-rendering together, which is what makes that surface crawl on a
      // phone. None of it is reachable there either: the whole interaction is a
      // right-click context menu on a 72px cell.
      if (!cellNoteConfig || isDense) {
        return content
      }
      return <CellWithNotes cellNoteConfig={cellNoteConfig}>{content}</CellWithNotes>
    }, [cellNoteConfig, children, isDense, isTableHead])

    return (
      <TableCellContext.Provider value={{ isActive, resetActiveCell }}>
        <MuiTableCell
          ref={cellRef}
          onClick={handleClickInside}
          classes={{
            root: classNames,
            head: styles.headRow,
          }}
          {...props}
        >
          {cellContent}
        </MuiTableCell>
      </TableCellContext.Provider>
    )
  },
)

export default TableCell
